/**
 * Imports
 */
import { gsap } from 'gsap';
import { Observer } from 'gsap/Observer';
import CustomEvent from 'core/lib/CustomEvent';

/**
 * The main class
 */
export default new class Illustration extends CustomEvent {
	/**
	 * Collection of custom events name
	 *
	 * @type {Object} Collection of custom events name
	 */
	E = {
		RAF: 'raf',
	};

	/**
	 * @type {number}
	 */
	INERTIA = 0.09;

	/**
	 * @type {number}
	 */
	maxX = 1;

	/**
	 * @type {number}
	 */
	maxY = 1;

	/**
	 * Returns a promise to make sure all is ready
	 *
	 * @return {Promise} Promise
	 */
	init() {
		this.$illustration = document.querySelector( '#illustration' );
		this.$layers = this.$illustration.querySelectorAll( 'img' );
		this.currentPosValues = { x: 0, y: 0 };

		this.#initLayers();
		this.#bindEvents();
		this.#initAutoMove();
	}

	#initLayers() {
		this.layers = [];
		this.$layers.forEach( ( layer, index ) => {
			this.layers.push( {
				layer: layer,
				percentX: index / ( this.$layers.length - 1 ),
				percentY: index / ( this.$layers.length - 1 ),
			} );
		} );

	}

	#bindEvents() {
		Observer.create( {
			target: window, // can be any element (selector text is fine)
			type: 'pointer,touch', // comma-delimited list of what to listen for
			onMove: this.#onMove.bind( this ), onDrag: this.#onMove.bind( this ),
		} );
		window.addEventListener( 'resize', this.#resize.bind( this ) );
		this.#resize();
	}

	#resize() {
		this.maxX = this.$illustration.clientWidth * 0.05 / 2;
		this.maxY = this.$illustration.clientHeight * 0.05 / 2;
	}

	#initAutoMove() {
		this.autoMoveTimeline = gsap.timeline( {
			yoyo: false,
			repeat: -1,
			repeatDelay: 1,
			repeatRefresh: true,
			onUpdate: this.#update.bind( this ),
		} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 0,
				y: 0,
			} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 0.5,
				y: 0.5,
			} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 0,
				y: 1,
			} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 0.5,
				y: 0.5,
			} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 1,
				y: 1,
			} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 0.5,
				y: 0.5,
			} )
			.to( this.currentPosValues, {
				duration: 0.7,
				ease: 'none',
				x: 1,
				y: 0,
			} )
			.to( this.currentPosValues, {
				duration: 0.7,
				ease: 'none',
				x: 0.5,
				y: 0.5,
			} );
	}

	#onMove( ref ) {
		this.currentPosValues.x = ( ( ref.x / window.innerWidth ) );
		this.currentPosValues.y = ( ref.y / window.innerHeight );
		this.#update();
		if ( this.autoMoveDelay ) {
			this.autoMoveDelay.kill();
			this.autoMoveTimeline.pause();
		}
		this.autoMoveDelay = gsap.delayedCall( 1, () => {
			this.autoMoveTimeline.invalidate().resume();
		} );
	}

	#update() {
		this.layers.forEach( ( layer, index ) => {
			gsap.to( layer.layer, {
				duration: 1,
				x: ( this.currentPosValues.x - 0.5 ) * ( layer.percentX * 2 ) * this.maxX,
				y: ( this.currentPosValues.y - 0.5 ) * ( layer.percentY * 2 ) * this.maxY,
			} );
		} );
	}

};
